import { FlipVertical2, RotateCcw, Scissors, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { SectionAxis } from '@/viewer/section';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';
import { cn } from '@/lib/cn';

const AXES: SectionAxis[] = ['x', 'y', 'z'];

export function SectionPanel() {
  const section = useViewerStore((s) => s.section);
  const sectionOpen = useViewerStore((s) => s.sectionOpen);
  const modelName = useViewerStore((s) => s.modelName);
  const treeOpen = useViewerStore((s) => s.treeOpen);
  const { toggleSection, updateSection } = useViewer();

  if (!sectionOpen || modelName === null) return null;

  const range = section.max - section.min;
  const step = range > 0 ? range / 200 : 1;
  // scene space is metres (web-ifc normalises units)
  const offsetLabel = `${section.offset.toFixed(2)} m`;

  return (
    <div
      className={cn(
        'pointer-events-auto absolute top-1/2 z-20 w-56 -translate-y-1/2 rounded-lg border border-panel-border bg-panel/90 p-3 shadow-2xl backdrop-blur',
        treeOpen ? 'left-[22.5rem]' : 'left-[3.75rem]',
      )}
    >
      <header className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Scissors className="h-4 w-4 text-accent" />
          Section
        </div>
        <Button variant="ghost" size="icon" onClick={toggleSection} aria-label="Close section panel">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="mb-2 flex items-center gap-1">
        {AXES.map((axis) => (
          <button
            key={axis}
            type="button"
            onClick={() => updateSection({ axis })}
            className={cn(
              'flex-1 rounded-md border px-2 py-1 text-xs uppercase transition-colors',
              section.axis === axis
                ? 'border-accent/60 bg-accent/20 text-accent'
                : 'border-panel-border text-muted hover:bg-white/6 hover:text-foreground',
            )}
          >
            {axis}
          </button>
        ))}
        <button
          type="button"
          onClick={() => updateSection({ flip: !section.flip })}
          title="Flip kept side"
          aria-label="Flip kept side"
          className={cn(
            'rounded-md border px-2 py-1 transition-colors',
            section.flip
              ? 'border-accent/60 bg-accent/20 text-accent'
              : 'border-panel-border text-muted hover:bg-white/6 hover:text-foreground',
          )}
        >
          <FlipVertical2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <input
        type="range"
        min={section.min}
        max={section.max}
        step={step}
        value={section.offset}
        onChange={(e) => updateSection({ offset: Number(e.target.value) })}
        aria-label="Section offset"
        className="w-full accent-accent"
      />
      <div className="mt-1 flex items-center justify-between text-xs text-muted">
        <span>{offsetLabel}</span>
        <button
          type="button"
          onClick={() => updateSection({ offset: section.flip ? section.min : section.max })}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors hover:bg-white/8 hover:text-foreground"
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      </div>
    </div>
  );
}
